import { elements, listsV2, feedbackV2, feedback } from '@ecosystem/newron-design-system';
import React, { useMemo } from 'react';

import { commonTableCellConfig } from '../../../utils/constants';
import { useGroupedTableData } from './hooks/useGroupedTableData';
import { BranchConfig, Group, GroupInfo, RootConfig, TableType } from './types';
import { getGroupColumnConfig, prepareColumns } from './utils';
const { AlertSize } = feedback;

const { Alert } = feedbackV2;
/**
 * A flexible tree-based table UI that supports grouping, sorting, and row selection.
 *
 * @param rawData - Flat data array to be grouped and rendered.
 * @param rootConfigOptions - Optional settings for the left pane.
 * @param branchConfigOptions - Right pane config for columns, selection, and interactions.
 */
export interface TreeTableProps<TData extends object> {
  rawData: TData[];
  rootConfigOptions: RootConfig<TData>;
  branchConfigOptions: BranchConfig;
  searchTerm?: string;
}
export const NO_SEARCH_RESULTS_MESSAGE = 'No results can be found';

const { Table } = listsV2;
const { Icon } = elements;
const { ChevronRight } = Icon.icons;

export const TreeTable = <TData extends object>({
  rawData,
  rootConfigOptions: rootConfig,
  branchConfigOptions: branchConfig,
  searchTerm = '',
}: TreeTableProps<TData>) => {
  const {
    groupBy,
    header,
    isSortingEnabled,
    onSelectGroup: onSelectGroupInternal,
    selectedGroup: selectedGroupInternal,
  } = rootConfig;
  const { columns, rowSelectionMode, selectedRows, onRowClick, onRowSelectionChange, disableSelectAllButton } =
    branchConfig;

  if (rowSelectionMode === listsV2.RowSelectionMode.Single && selectedRows) {
    const keys = Object.keys(selectedRows ?? {});
    if (keys.length > 1) {
      throw new Error(`Invalid selectedRows: rowSelectionMode is 'single', but received ${keys.length} selected rows.`);
    }
  }

  const { groups, selectedGroup, branchData, onSelectGroup, onSortingChange } = useGroupedTableData({
    data: rawData,
    groupBy,
    getGroupName: rootConfig.getDisplayValue,
    getGroupSortingValue: rootConfig.getSortingValue,
    selectedGroup: selectedGroupInternal,
    columnConfig: columns,
    onSelectGroup: onSelectGroupInternal,
    searchTerm,
  });

  return (
    <div className="grid grid-cols-6 gap-3 h-full overflow-hidden">
      <div className="col-span-2 overflow-y-auto">
        <RootTable
          header={header}
          groups={groups}
          isSortingEnabled={isSortingEnabled}
          selectedGroup={selectedGroup}
          onSelectGroup={onSelectGroup}
          onSortingChange={onSortingChange(TableType.Root)}
        />
      </div>

      <div className="col-span-4 overflow-auto">
        {branchData?.length ? (
          <Table<TData>
            data={branchData}
            columnConfig={prepareColumns(columns, groupBy)}
            selectorCellConfig={commonTableCellConfig}
            isRowSelectable={!!rowSelectionMode}
            rowSelectionMode={rowSelectionMode}
            selectedRows={selectedRows}
            onRowSelectionChange={onRowSelectionChange}
            onSortingChange={onSortingChange(TableType.Branch)}
            onRowClick={onRowClick}
            disableSelectAllButton={disableSelectAllButton}
          />
        ) : (
          <Alert type="info" size={AlertSize.SMALL} title={NO_SEARCH_RESULTS_MESSAGE} />
        )}
      </div>
    </div>
  );
};

interface RootTableProps {
  header: string;
  groups: GroupInfo[];
  isSortingEnabled?: boolean;
  selectedGroup: string;
  onSelectGroup: (group: string) => void;
  onSortingChange?: (sorting: listsV2.SortingChangeProps) => void;
}

const GroupedColumnSubComponent = React.memo(({ cellData }: { cellData: Group }): JSX.Element => {
  const { label, count } = cellData;
  return (
    <div className="grid grid-cols-4 items-center h-full w-full">
      <div className="col-span-3 gap-1 flex">
        <p className="truncate text-md" title={label}>
          {label}
        </p>
        <p className="text-md">({count})</p>
      </div>
      <div className="col-span-1 justify-self-end">
        <Icon color="secondaryInteractive">
          <ChevronRight />
        </Icon>
      </div>
    </div>
  );
});

const rootTableSubComponent = {
  render: (cellData: Group) => <GroupedColumnSubComponent cellData={cellData} />,
};

const RootTable: React.FC<RootTableProps> = ({
  header,
  groups,
  selectedGroup,
  isSortingEnabled = false,
  onSelectGroup,
  onSortingChange,
}) => {
  const selectedRow = useMemo(() => ({ [selectedGroup]: true }), [selectedGroup]);

  const groupColumnConfig = useMemo<listsV2.ColumnConfig[]>(
    () => [
      {
        ...getGroupColumnConfig(header, isSortingEnabled),
        subComponent: cellData => rootTableSubComponent.render(cellData),
      },
    ],
    [header, isSortingEnabled]
  );

  return (
    <Table<GroupInfo>
      data={groups}
      columnConfig={groupColumnConfig}
      selectedRows={selectedRow}
      onRowClick={({ rowData }) => {
        onSelectGroup(rowData.id);
      }}
      onSortingChange={onSortingChange}
      highlightRowOnClick
    />
  );
};
